import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Layers, Pencil, Plus, Trash2 } from "lucide-react";

import { api, type CameraProfile } from "../../api";
import { CameraProfileDialog } from "../../components/CameraProfileDialog";
import { Modal } from "../../components/Modal";
import { useScope } from "../../components/ScopeContext";
import { useWords } from "../../words";
import { EmptyState, SettingsSection } from "./shared";

/**
 * Camera profiles: reusable groups that can span stores.
 *
 * "All entrance cameras" is a real thing an operator wants to watch, and it
 * does not respect store boundaries. A profile is selectable anywhere a store
 * is, and each member keeps its own store context.
 */
export function ProfilesSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { selectProfile, scope, stores } = useScope();
  const w = useWords();
  const storeLabel = (name: string) =>
    stores.find((s) => s.name === name)?.display_name || name;
  const profiles = useQuery({ queryKey: ["profiles"], queryFn: api.profiles.list });
  const [draft, setDraft] = useState<CameraProfile | "new" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CameraProfile | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => api.profiles.delete(id),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["profiles"] });
    },
  });

  const list = profiles.data ?? [];

  return (
    <SettingsSection
      title={w.Groups}
      description={`Named groups of cameras, which may span ${w.stores}.`}
      hint={
        <>
          Pick a {w.group} in the <b>Select {w.store}</b> picker to see exactly
          those cameras, wherever they are. Each camera keeps its own {w.store},
          so a {w.group} spanning three sites still files its alerts and footage
          under the right one.
        </>
      }
      actions={
        canEdit && (
          <button className="bf-button" onClick={() => setDraft("new")}>
            <Plus size={16} className="mr-2" /> New {w.group}
          </button>
        )
      }
    >
      {profiles.isLoading ? (
        <div className="bf-card text-sm text-bf-muted">Loading {w.groups}…</div>
      ) : list.length === 0 ? (
        <EmptyState
          icon={Layers}
          title={`No ${w.groups} yet`}
          hint={`Group the cameras you keep coming back to — “All entrance cameras”, “Checkouts”, “Loading bays” — even when they are at different ${w.stores}.`}
          action={
            canEdit && (
              <button className="bf-button" onClick={() => setDraft("new")}>
                <Plus size={16} className="mr-2" /> Create a {w.group}
              </button>
            )
          }
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {list.map((p) => (
            <article
              key={p.id}
              className={
                "bf-card space-y-3 " +
                (scope.kind === "profile" && scope.profileId === p.id
                  ? "ring-1 ring-inset ring-bf-violet/50"
                  : "")
              }
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-white">
                    {p.name}
                  </h3>
                  {p.description && (
                    <p className="truncate text-xs text-bf-muted">{p.description}</p>
                  )}
                </div>
                <span className="shrink-0 rounded-full bg-white/5 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-bf-muted ring-1 ring-inset ring-white/10">
                  {p.members.length} camera{p.members.length === 1 ? "" : "s"}
                </span>
              </div>

              {p.stores.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {p.stores.map((s) => (
                    <span
                      key={s}
                      className="rounded-full bg-bf-violet/15 px-2 py-0.5 text-xs text-bf-text"
                    >
                      {storeLabel(s)}
                    </span>
                  ))}
                </div>
              )}

              <ul className="space-y-0.5 text-xs text-bf-muted">
                {p.members.slice(0, 4).map((m) => (
                  <li key={m.camera_id} className="truncate">
                    {m.name}
                    <span className="text-bf-muted/60">
                      {" "}
                      · {m.store ? storeLabel(m.store) : `no ${w.store}`}
                    </span>
                  </li>
                ))}
                {p.members.length > 4 && <li>… and {p.members.length - 4} more</li>}
                {p.members.length === 0 && <li>No cameras in this {w.group} yet.</li>}
              </ul>

              {p.missing.length > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-amber-300">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  {p.missing.length} camera{p.missing.length === 1 ? "" : "s"} in
                  this {w.group} {p.missing.length === 1 ? "has" : "have"} been
                  deleted and {p.missing.length === 1 ? "is" : "are"} skipped.
                  Edit the {w.group} to tidy it up.
                </p>
              )}

              <div className="flex items-center justify-between gap-2 border-t border-white/5 pt-3">
                <button
                  type="button"
                  className="text-xs text-bf-muted hover:text-bf-text disabled:opacity-40"
                  onClick={() => selectProfile(p.id)}
                  disabled={p.members.length === 0}
                >
                  View this {w.group}
                </button>
                {canEdit && (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="text-bf-muted hover:text-bf-text"
                      aria-label={`Edit ${p.name}`}
                      onClick={() => setDraft(p)}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      className="text-bf-muted hover:text-red-400"
                      aria-label={`Delete ${p.name}`}
                      onClick={() => setConfirmDelete(p)}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {draft && (
        <CameraProfileDialog
          profile={draft === "new" ? null : draft}
          onClose={() => setDraft(null)}
        />
      )}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} panelClassName="w-full max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">
              Delete “{confirmDelete.name}”?
            </h3>
            <p className="text-sm text-bf-muted">
              The {w.group} is only a grouping — its cameras, their footage and
              their settings are untouched.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="bf-button bg-white/10 bg-none"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={remove.isPending}
                onClick={() => remove.mutate(confirmDelete.id)}
              >
                {remove.isPending ? "Deleting…" : `Delete ${w.group}`}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </SettingsSection>
  );
}
